import React from "react";
import { Link } from "react-router-dom";
import { PERIODS, findYear, linkFor } from "../../../constants/catalogue";
import { C, FONT } from "../../../constants/mq";

// When each course of a year level runs, across the school year: the terms
// with the holidays between them. Only years whose courses say `when` (Year 6)
// have a plan; for the rest this renders nothing.
//
// A course made of several subjects (High School Preparation) lists them as
// rows under it, so a parent sees the three subjects it is made of.

// A touch darker than the sand panels the plan sits on, so it still shows there.
const HOLIDAY = "#EEEBE3";

const s = {
  wrap: { fontFamily: FONT },
  head: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, margin: "0 0 10px" },
  title: { fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 },
  state: { fontSize: 12, color: C.muted },
  // Fixed columns, so all eight periods fit on a phone without scrolling.
  table: { width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13, background: C.white },
  th: {
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    padding: "8px 2px",
    textAlign: "center",
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: "nowrap",
  },
  thHoliday: { background: HOLIDAY },
  name: { padding: "9px 10px 9px 12px", color: C.ink, fontWeight: 600, borderBottom: `1px solid ${C.line}`, textAlign: "left" },
  part: { padding: "7px 10px 7px 26px", color: C.body, fontWeight: 400, borderBottom: `1px solid ${C.line}`, textAlign: "left" },
  cell: { textAlign: "center", borderBottom: `1px solid ${C.line}`, padding: "6px 2px" },
  holidayCell: { background: HOLIDAY },
  dot: { display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: C.red },
  partDot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: C.red, opacity: 0.55 },
  link: { color: C.ink, textDecoration: "none" },
  note: { fontSize: 12, color: C.muted, margin: "8px 0 0", lineHeight: 1.6 },
};

// Phones get one-letter period headings (S 1 A 2 W 3 S 4) and a smaller
// name column.
const css = `
.yp-short { display: none; }
@media (max-width: 560px) {
  .yp-long { display: none; }
  .yp-short { display: inline; }
  .yp-table { font-size: 12px !important; }
  .yp-table th[scope="row"] { padding-left: 8px !important; }
  .yp-table th[scope="row"].yp-part { padding-left: 16px !important; }
}
`;

const YearPlan = ({ year, title }) => {
  const entry = findYear(year);
  const courses = (entry?.subjects || []).filter((sub) => sub.when?.length);
  if (!courses.length) return null;

  const row = (label, when, { part = false, href = null } = {}) => (
    <tr key={label}>
      <th scope="row" className={part ? "yp-part" : undefined} style={part ? s.part : s.name}>
        {href ? (
          <Link to={href} style={s.link}>
            {label}
          </Link>
        ) : (
          label
        )}
      </th>
      {PERIODS.map((p) => (
        <td
          key={p.key}
          style={{ ...s.cell, ...(p.holiday ? s.holidayCell : null) }}
          title={when.includes(p.key) ? `${label}: ${p.label}` : undefined}
        >
          {when.includes(p.key) && <span style={part ? s.partDot : s.dot} />}
        </td>
      ))}
    </tr>
  );

  return (
    <div style={s.wrap}>
      <style>{css}</style>
      <div style={s.head}>
        <p style={s.title}>{title || `Year ${year} through the year`}</p>
        {entry.plan && <span style={s.state}>{entry.plan}</span>}
      </div>
      <div>
        <table className="yp-table" style={s.table}>
          <colgroup>
            <col style={{ width: "38%" }} />
            {PERIODS.map((p) => (
              <col key={p.key} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...s.th, textAlign: "left", paddingLeft: 12 }}>Course</th>
              {PERIODS.map((p) => (
                <th
                  key={p.key}
                  style={{ ...s.th, ...(p.holiday ? s.thHoliday : null) }}
                  title={p.label}
                >
                  <span className="yp-long">{p.short}</span>
                  <span className="yp-short">{p.letter}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {courses.flatMap((sub) => [
              row(sub.label || sub.name, sub.when, { href: linkFor(year, sub.name)?.href }),
              ...(sub.parts || []).map((part) => row(part.name, sub.when, { part: true })),
            ])}
          </tbody>
        </table>
      </div>
      <p style={s.note}>
        Shaded columns are the school holidays: Summer, Autumn, Winter and Spring.
      </p>
    </div>
  );
};

export default YearPlan;
